import React from "react";
import { useParams } from "react-router-dom";

// Components
// import SEO from "components/CBSEO";
// import SchemaComponent from "components/CBSchema";
import DynamicHero from "components/DynamicHero";
import TextBlock from "components/TextBlock";
import { ImageContainer } from "styles/components/imageContainer";

// Styled Components
import { StyledPageContainer } from "styles/components/PageContainer/index.styles";
import { TwoColumnSection } from "styles/components/twoColumnSection";
// Utils
import { constructSchemaData } from "utils/helpers/constructSchemaData";

// Hook
import useDynamicPageRenderer from "utils/hooks/useDynamicPageRenderer";

// Content Data
import depressionContent from "./data/pageContentData/depressionContent.data.json";
import generalizedAnxietyDisorderContent from "./data/pageContentData/generalizedAnxietyDisorderContent.data.json";
import socialAnxietyDisorderContent from "./data/pageContentData/socialAnxietyDisorderContent.data.json";
import treatmentResistantDepressionContent from "./data/pageContentData/treatmentResistantDepressionContent.data.json";
import bipolarDisorderContent from "./data/pageContentData/bipolarDisorderContent.data.json";
import postTraumaticStressDisorderContent from "./data/pageContentData/postTraumaticStressDisorderContent.data.json";
import attentionDeficitHyperactivityDisorderContent from "./data/pageContentData/attentionDeficitHyperactivityDisorderContent.data.json";
import obsessiveCompulsiveDisorderContent from "./data/pageContentData/obsessiveCompulsiveDisorderContent.data.json";
import insomniaContent from "./data/pageContentData/insomniaContent.data.json";
import panicDisorderContent from "./data/pageContentData/panicDisorderContent.data.json";

// SEO Data
import depressionSeoData from "./data/seoData/depressionSeo.data.json";
import generalizedAnxietyDisorderSeoData from "./data/seoData/generalizedAnxietyDisorderSeo.data.json";
import socialAnxietyDisorderSeoData from "./data/seoData/socialAnxietyDisorderSeo.data.json";
import treatmentResistantDepressionSeoData from "./data/seoData/treatmentResistantDepressionSeo.data.json";
import bipolarDisorderSeoData from "./data/seoData/bipolarDisorderSeo.data.json";
import postTraumaticStressDisorderSeoData from "./data/seoData/postTraumaticStressDisorderSeo.data.json";
import attentionDeficitHyperactivityDisorderSeoData from "./data/seoData/attentionDeficitHyperactivityDisorderSeo.data.json";
import obsessiveCompulsiveDisorderSeoData from "./data/seoData/obsessiveCompulsiveDisorderSeo.data.json";
import insomniaSeoData from "./data/seoData/insomniaSeo.data.json";
import panicDisorderSeoData from "./data/seoData/panicDisorderSeo.data.json";

// Schema Data
import depressionSchemaData from "./data/schemaData/depressionSchema.data.json";
import generalizedAnxietyDisorderSchemaData from "./data/schemaData/generalizedAnxietyDisorderSchema.data.json";
import socialAnxietyDisorderSchemaData from "./data/schemaData/socialAnxietyDisorderSchema.data.json";
import treatmentResistantDepressionSchemaData from "./data/schemaData/treatmentResistantDepressionSchema.data.json";
import bipolarDisorderSchemaData from "./data/schemaData/bipolarDisorderSchema.data.json";
import postTraumaticStressDisorderSchemaData from "./data/schemaData/postTraumaticStressDisorderSchema.data.json";
import attentionDeficitHyperactivityDisorderSchemaData from "./data/schemaData/attentionDeficitHyperactivityDisorderSchema.data.json";
import obsessiveCompulsiveDisorderSchemaData from "./data/schemaData/obsessiveCompulsiveDisorderSchema.data.json";
import insomniaSchemaData from "./data/schemaData/insomniaSchema.data.json";
import panicDisorderSchemaData from "./data/schemaData/panicDisorderSchema.data.json";
import Page404 from "pages/404";
import SEO from "components/CBSEO";
import SchemaComponent from "components/CBSchema";
import InsuranceSection from "components/InsuranceSection";
import StartYourJourney from "pages/TherapyHub/components/startYourJourney";
import FAQContent from "components/CBFAQ";
import GetStartedToday from "pages/TherapyHub/components/GetStartedToday";
import Testimonials from "components/Testimonials";
import CBDivider from "components/CBDivider";
import ResourcesSlider from "pages/TherapyHub/components/resourcesSlider";

// Data Maps - mapping treatmentType to respective data
const treatmentContentMap = {
  "depression": depressionContent,
  "generalized-anxiety-disorder": generalizedAnxietyDisorderContent,
  "social-anxiety-disorder": socialAnxietyDisorderContent,
  "treatment-resistant-depression": treatmentResistantDepressionContent,
  "bipolar-disorder": bipolarDisorderContent,
  "post-traumatic-stress-disorder": postTraumaticStressDisorderContent,
  "attention-deficit-hyperactivity-disorder": attentionDeficitHyperactivityDisorderContent,
  "obsessive-compulsive-disorder": obsessiveCompulsiveDisorderContent,
  "insomnia": insomniaContent,
  "panic-disorder": panicDisorderContent,
};

const treatmentSeoDataMap = {
  "depression": depressionSeoData,
  "generalized-anxiety-disorder": generalizedAnxietyDisorderSeoData,
  "social-anxiety-disorder": socialAnxietyDisorderSeoData,
  "treatment-resistant-depression": treatmentResistantDepressionSeoData,
  "bipolar-disorder": bipolarDisorderSeoData,
  "post-traumatic-stress-disorder": postTraumaticStressDisorderSeoData,
  "attention-deficit-hyperactivity-disorder": attentionDeficitHyperactivityDisorderSeoData,
  "obsessive-compulsive-disorder": obsessiveCompulsiveDisorderSeoData,
  "insomnia": insomniaSeoData,
  "panic-disorder": panicDisorderSeoData,
};

const treatmentSchemaDataMap = {
  "depression": depressionSchemaData,
  "generalized-anxiety-disorder": generalizedAnxietyDisorderSchemaData,
  "social-anxiety-disorder": socialAnxietyDisorderSchemaData,
  "treatment-resistant-depression": treatmentResistantDepressionSchemaData,
  "bipolar-disorder": bipolarDisorderSchemaData,
  "post-traumatic-stress-disorder": postTraumaticStressDisorderSchemaData,
  "attention-deficit-hyperactivity-disorder": attentionDeficitHyperactivityDisorderSchemaData,
  "obsessive-compulsive-disorder": obsessiveCompulsiveDisorderSchemaData,
  "insomnia": insomniaSchemaData,
  "panic-disorder": panicDisorderSchemaData,
};

// Components that expect props to be spread directly
const SPREAD_PROPS_COMPONENTS = ["DynamicHero", "TextBlock", "ThreeColumnSection", "Testimonials", "HowItWorks"];

// Component Map - mapping component type strings to React components
const componentMap = {
  "DynamicHero": DynamicHero,
  "TextBlock": TextBlock,
  "TwoColumnSection": TwoColumnSection,
  "ImageContainer": ImageContainer,
  "InsuranceSection": InsuranceSection,
  "StartYourJourney": StartYourJourney,
  "Faq": FAQContent,
  "GetStartedToday": GetStartedToday,
  "Testimonials": Testimonials,
  "Divider": CBDivider,
  "ResourcesSlider": ResourcesSlider,
};

const TreatmentDetails = () => {
  const { treatmentType } = useParams();
  const content = treatmentContentMap[treatmentType] || [];
  const seoData = treatmentSeoDataMap[treatmentType] || {};
  const schemaDataRaw = treatmentSchemaDataMap[treatmentType] || {};
  const schemaData = schemaDataRaw.url ? constructSchemaData(schemaDataRaw.url, schemaDataRaw.breadcrumbs) : [];
  const renderedComponents = useDynamicPageRenderer(content, componentMap, SPREAD_PROPS_COMPONENTS);

  if (!content || content.length === 0) {
    return <Page404 />;
  }
  return (
    <StyledPageContainer id={`${treatmentType}-treatment-details-page-container`}>
      <SEO title={seoData.title} description={seoData.description} canonicalUrl={seoData.canonicalUrl} />
      <SchemaComponent schemas={schemaData} />

      {renderedComponents}
    </StyledPageContainer>
  );
};

export default TreatmentDetails;
